/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import { compose, withHandlers } from 'recompose'
import { connect } from 'react-redux'
import { reduxForm, initialize, getFormValues } from 'redux-form'
import { createSelector } from 'reselect'
import _get from 'lodash/get'
import _omit from 'lodash/omit'
import { analytics, SITE_SECTIONS } from 'services/Analytics'
import { ERROR_CODES } from 'constants/errors'
import { ALERTS } from 'constants/alerts'
import { USER_FORMS } from 'constants/forms'
import { EMPTY_OBJECT } from 'constants/frozenObjects'
import { withLocalization } from 'hocs/localization'
import { formWithAddress, FORM_COUNTRY_TYPES } from 'hocs/formWithAddress'
import { onSaveBillingAddress, onCompleteSavingBillingAddress } from 'redux-app/actionCreators/subscription'
import { onShowError, createAlert } from 'redux-app/modules/alerts'
import { openModal } from 'redux-app/modules/modals'
import { putMeCompanyCardBillingAddress, getMeCompanyCardBillingAddress } from 'api/client/actions/company'
import { getUserDetails, isUserUnableToUpdateInfo } from 'redux-app/selectors/user'
import { correctedBillingAddressModal } from 'apps/Profile/CorrectedAddressModal'
import { getErrorCodes } from 'utils/error'
import { removeDashes } from 'utils/form'
import { getPrintedPostalCode } from 'utils/printedData'
import BillingInfoForm from './BillingInfoForm'

const FORM_NAME = USER_FORMS.profileBillingForm
const SHIPPING_FORM_NAME = USER_FORMS.profileShippingForm

const getInitialValues = createSelector(
  (state, props) => _get(getUserDetails(state), 'billingAddress', EMPTY_OBJECT),
  (state, props) => getFormValues(SHIPPING_FORM_NAME)(state),
  (state, props) => props.isBillingCheckboxChecked,
  (state, props) => props.isEdit,
  (billingAddress, shippingAddress, isSameValues, isEdit) => {
    if (isSameValues && !isEdit) {
      return shippingAddress
    }

    return {
      ...billingAddress,
      postalCode: getPrintedPostalCode(_get(billingAddress, 'postalCode')),
    }
  },
)

export default compose(
  connect(
    (state, props) => ({
      initialValues: getInitialValues(state, props),
      isUserUnableToUpdateInfo: isUserUnableToUpdateInfo(state),
      postalCode: _get(getFormValues(FORM_NAME)(state), 'postalCode', ''),
    }),
    {
      onUpdateBillingInfo: putMeCompanyCardBillingAddress,
      onShowError,
      createAlert,
      getMeCompanyCardBillingAddress,
      openModal,
      initialize,
      onSaveBillingAddress,
      onCompleteSavingBillingAddress,
    },
  ),
  withLocalization('company.paymentInfo.billing'),
  withHandlers({
    onSubmit: ({
      onSaveBillingAddress,
      onShowError,
      createAlert,
      openModal,
      onCompleteSavingBillingAddress,
    }) => ({ postalCode, ...restFormValues }) => {
      const formValues = {
        ...restFormValues,
        postalCode: removeDashes(postalCode),
      }
      return onSaveBillingAddress(formValues)
        .then(({ value: { result } }) => {
          if (!result.isMatch) {
            const data = {
              shippingCorrectedResult: _omit(result, ['isMatch']),
            }
            openModal({ name: correctedBillingAddressModal.name, data })
            return
          }
          createAlert({ name: ALERTS.successBillingInfoChanged })
          analytics.saveChanges({ sectionName: SITE_SECTIONS.billingAddress })
          onCompleteSavingBillingAddress()
        })
        .catch((error: Error) => {
          analytics.showMyAccountError({
            errorCodes: getErrorCodes(error),
            sectionType: SITE_SECTIONS.billingAddress,
          })
          if (getErrorCodes(error).includes(ERROR_CODES.addressIsNotValid)) {
            createAlert({ name: ALERTS.billingAddressIsNotValid })
          } else {
            onShowError(error, formValues)
          }
        })
    },
    onConfirmCorrectedAddress: ({
      onSaveBillingAddress,
      onShowError,
      createAlert,
      onCompleteSavingBillingAddress,
      initialize,
    }) => ({ shippingCorrectedResult }) => {
      return onSaveBillingAddress(shippingCorrectedResult)
        .then(({ value: { result } }) => {
          if (!result.isMatch) {
            createAlert({ name: ALERTS.profileBillingFormError })
            return
          }
          initialize(FORM_NAME, {
            ...result,
            postalCode: getPrintedPostalCode(result.postalCode),
          })
          createAlert({ name: ALERTS.successBillingInfoChanged })
          analytics.saveChanges({ sectionName: SITE_SECTIONS.billingAddress })
          onCompleteSavingBillingAddress()
        })
        .catch(error => {
          analytics.showMyAccountError({
            errorCodes: getErrorCodes(error),
            sectionType: SITE_SECTIONS.billingAddress,
          })
          onShowError(error)
        })
    },
  }),
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
  }),
  formWithAddress(FORM_COUNTRY_TYPES.billing),
)(BillingInfoForm)
